import logo from "assets/images/integrations/klaviyo_logo.png";
import { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { useOpenFeatureFlag } from "shared/hooks";
import { RootReduxState } from "../../../../../utils/types";
import { AvailableIntegrationCardAddButton } from "../../components/AvailableIntegrationCardAddButton";
import { AvailableIntegrationCardExplanationWrapper } from "../../components/AvailableIntegrationCardExplanationWrapper";
import { AvailableIntegrationCardHeader } from "../../components/AvailableIntegrationCardHeader";
import { AvailableIntegrationCardWrapper } from "../../components/AvailableIntegrationCardWrapper";
import { ConnectViewComponentProps, IntegrationType } from "../../types";
import { KlaviyoConnectModal } from "./connect-modal";
import {
  KlaviyoDataReduxState,
  KlaviyoRequestReduxState,
  getKlaviyoIntegrationSettingsAction
} from "./redux";

type KlaviyoConnectViewComponentOwnProps = ConnectViewComponentProps;

const mapStateToProps = (state: RootReduxState) => {
  const selectedShopId = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShopId"
  ]) as string;

  const getRequestState = state.getIn(["integrations", "klaviyo", "requests", "get"])?.toJS() as
    | KlaviyoRequestReduxState
    | undefined;

  return {
    selectedShopId: Number(selectedShopId),
    isLoading: getRequestState?.loading ?? false,
    integrationData: state.getIn(["integrations", "klaviyo", "data"])?.toJS() as
      | KlaviyoDataReduxState
      | undefined
  };
};
const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getKlaviyoIntegrationSettingsAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type KlaviyoConnectViewComponentProps = KlaviyoConnectViewComponentOwnProps &
  ConnectedProps<typeof connector>;

const KlaviyoConnectViewComponent = (props: KlaviyoConnectViewComponentProps) => {
  const {
    connectingType,
    onStartConnecting,
    onStopConnecting,
    integrationData,
    getKlaviyoIntegrationSettingsAction,
    selectedShopId,
    isLoading
  } = props;

  const { value: integrationFeatureFlagEnabled } = useOpenFeatureFlag(
    "klaviyo_integration_setup_enabled"
  );

  useEffect(() => {
    getKlaviyoIntegrationSettingsAction(selectedShopId);
  }, []);

  const handleConnect = useCallback(() => {
    onStartConnecting(IntegrationType.KLAVIYO);
  }, [onStartConnecting]);

  const isConnected = integrationData?.settings !== undefined;

  if (!isConnected && !integrationFeatureFlagEnabled) {
    return null;
  }

  return (
    <AvailableIntegrationCardWrapper>
      {connectingType === IntegrationType.KLAVIYO && (
        <KlaviyoConnectModal handleClose={onStopConnecting} />
      )}
      <AvailableIntegrationCardHeader image={logo} text="Klaviyo" invertLogo />
      <AvailableIntegrationCardExplanationWrapper>
        <FormattedMessage
          id="containers.Integrations.ListOfApps.Shared.Short.Explanation"
          defaultMessage="Connect your Klaviyo account with uptain. Then new newsletter subscribers can be added to your account."
          values={{ tool: "Klaviyo" }}
        />
      </AvailableIntegrationCardExplanationWrapper>
      <AvailableIntegrationCardAddButton
        isConnected={isConnected}
        loading={isLoading || connectingType !== undefined}
        onConnect={handleConnect}
      />
    </AvailableIntegrationCardWrapper>
  );
};

export const KlaviyoConnectView = connector(KlaviyoConnectViewComponent);
